import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Zap, Copy, CheckCircle, Loader2, Key, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { useNavigate } from 'react-router-dom';
import { zapierApiKeyService, ZapierApiKey, CreateKeyResponse } from '../../../services/zapierApiKeyService';

export const ZapierNode: React.FC<{ data: { label: string; color: string; isTemplate?: boolean } }> = ({ data }) => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState<ZapierApiKey[]>([]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    zapierApiKeyService
      .listKeys()
      .then((k) => setKeys(k))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      const res = await zapierApiKeyService.createKey(keyName);
      setCreatedKey(res.api_key);
      setKeys((prev) => [{ key_id: res.key_id, api_key: res.api_key, name: res.name, created_at: res.created_at, is_active: true }, ...prev]);
      setKeyName('');
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  };

  const activeKey = keys.find((k) => k.is_active) || keys[0];
  const displayKey = createdKey || activeKey?.api_key;
  const maskedKey = displayKey ? zapierApiKeyService.maskKey(displayKey) : '';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[350px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Zap className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && !activeKey && (
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
              Zapier API Key
            </p>
            <p className="text-xs text-muted-foreground">No key generated yet</p>
            <div className="nodrag">
              <Input
                className="h-7 text-[11px]"
                placeholder="Key name (e.g. Production)"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs h-7"
              onClick={handleCreate}
              disabled={creating || !keyName.trim()}
            >
              {creating ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Key className="h-3 w-3 mr-1" />
              )}
              Create API Key
            </Button>
          </div>
        )}

        {!loading && activeKey && (
          <>
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Zapier API Key
              </p>
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[10px] font-mono px-1.5">
                  <Key className="h-2.5 w-2.5 mr-1" />
                  {maskedKey}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => displayKey && copyToClipboard(displayKey)}
                >
                  {copied ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Status
              </p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                  Active
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {zapierApiKeyService.formatDate(activeKey.created_at)}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Capabilities
              </p>
              <ul className="text-[10px] text-muted-foreground space-y-0.5">
                <li>Upload files via Zapier</li>
                <li>Query data from Zaps</li>
                <li>Trigger on new files</li>
              </ul>
            </div>
          </>
        )}

        <Button
          variant="link"
          size="sm"
          className="text-[11px] p-0 h-auto"
          onClick={() => navigate('/integrations')}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Open Integrations
        </Button>
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
    </div>
  );
};
